import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CalendarPlus, X } from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { apiClient } from '@/services/api';

interface Tenant {
  id: number;
  razao_social: string;
  status: string;
  status_reason?: string | null;
  trial_ends_at?: string | null;
  grace_ends_at?: string | null;
  trial_extensions: number;
}

interface Props {
  tenant: Tenant;
  onClose: () => void;
}

const PRESETS = [
  { days: 7,  label: '+7 dias' },
  { days: 14, label: '+14 dias' },
  { days: 30, label: '+30 dias' },
  { days: 60, label: '+60 dias' },
];

export function ExtendTrialModal({ tenant, onClose }: Props) {
  const qc = useQueryClient();
  const [days, setDays] = useState<number>(14);
  const [reason, setReason] = useState('');

  // Esc fecha
  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  const extendMut = useMutation({
    mutationFn: () => apiClient.admin.extendTrial(tenant.id, days),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-tenants'] });
      toast.success(`Trial estendido em ${days} dias`);
      onClose();
    },
    onError: (e: any) => {
      const msg = e?.response?.data?.message ?? 'Falha ao estender';
      toast.error(msg);
    },
  });

  // Pré-visualiza a nova data baseada em trial_ends_at futuro, senão hoje.
  const now = new Date();
  const baseDate = tenant.trial_ends_at && new Date(tenant.trial_ends_at) > now
    ? new Date(tenant.trial_ends_at) : now;
  const newDate = new Date(baseDate.getTime() + days * 24 * 3600 * 1000);

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div className="flex items-center gap-2">
            <CalendarPlus className="h-5 w-5 text-brand-600" />
            <h2 className="text-lg font-bold">Adiar trial</h2>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-muted rounded-md">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-5">
          {/* Tenant info */}
          <div className="bg-muted/30 rounded-lg p-3 text-sm">
            <div className="font-semibold">{tenant.razao_social}</div>
            <div className="text-xs text-muted-foreground mt-0.5">
              Status: <span className="font-mono">{tenant.status}</span>
              {tenant.status_reason && <> · <span className="font-mono">{tenant.status_reason}</span></>}
            </div>
            {tenant.trial_ends_at && (
              <div className="text-xs text-muted-foreground mt-0.5">
                Trial atual até: <strong>{new Date(tenant.trial_ends_at).toLocaleDateString('pt-BR')}</strong>
              </div>
            )}
            {tenant.trial_extensions > 0 && (
              <div className="text-xs text-amber-600 mt-1">
                ⚠️ Este tenant já foi estendido {tenant.trial_extensions}× antes
              </div>
            )}
          </div>

          {/* Presets */}
          <div>
            <label className="text-sm font-medium block mb-2">Quanto adiar?</label>
            <div className="grid grid-cols-4 gap-2">
              {PRESETS.map((p) => (
                <button
                  key={p.days}
                  onClick={() => setDays(p.days)}
                  className={`px-3 py-2 rounded-md text-sm font-medium border transition ${
                    days === p.days
                      ? 'bg-brand-600 text-white border-brand-600'
                      : 'bg-white text-foreground border-input hover:bg-muted'
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          {/* Custom days */}
          <div>
            <label className="text-sm font-medium block mb-1">Ou quantidade exata (1–90 dias)</label>
            <Input
              type="number"
              min={1}
              max={90}
              value={days}
              onChange={(e) => {
                const n = parseInt(e.target.value, 10);
                if (!isNaN(n)) setDays(Math.min(90, Math.max(1, n)));
              }}
            />
          </div>

          {/* Preview */}
          <div className="rounded-lg border border-brand-200 bg-brand-50 px-3 py-2 text-sm">
            <span className="text-muted-foreground">Novo vencimento: </span>
            <strong className="text-brand-700">
              {newDate.toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' })}
            </strong>
          </div>

          {/* Reason (apenas anotacao local — backend ainda nao persiste, mas fica no toast) */}
          <div>
            <label className="text-sm font-medium block mb-1">Motivo (opcional)</label>
            <Input
              placeholder="Ex: negociação em andamento, problema técnico, etc"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
            <p className="text-[11px] text-muted-foreground mt-1">
              Aparece nos logs e ajuda a justificar a extensão depois.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-border flex justify-end gap-2 bg-muted/10">
          <Button variant="ghost" onClick={onClose}>Cancelar</Button>
          <Button
            onClick={() => extendMut.mutate()}
            disabled={extendMut.isPending || days < 1}
          >
            {extendMut.isPending ? 'Adiando...' : `Adiar ${days} dia${days === 1 ? '' : 's'}`}
          </Button>
        </div>
      </div>
    </div>
  );
}
